import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Grid, Paper,
  IconButton, Chip, Rating, Stack, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Divider, Tooltip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, Share as ShareIcon } from '@mui/icons-material';
import { getMyWaxingDiary, addWaxingDiaryEntry, updateWaxingDiaryEntry, deleteWaxingDiaryEntry, getSharedTeams } from '../api';
import ShareWaxingEntryModal from './ShareWaxingEntryModal';
import WeatherDataForm from '../components/WeatherDataForm';
import WeatherDataDisplay from '../components/WeatherDataDisplay';
import WeatherFilter from '../components/WeatherFilter';
import { filterByWeatherConditions, hasActiveWeatherFilters } from '../utils/weatherFilterUtils';

export default function WaxingDiarySection() {
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [sharedMap, setSharedMap] = useState({});
  const [weatherFilters, setWeatherFilters] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    activity_type: 'training',
    location: '',
    // Standardized weather fields
    snow_temperature_c: '',
    air_temperature_c: '',
    snow_humidity_doser: '',
    air_humidity_rh: '',
    clouds_sky_parts: '',
    visibility: '',
    wind: '',
    precipitation: '',
    artificial_snow: '',
    natural_snow: '',
    grain_size: '',
    snow_humidity: '',
    track_hardness: '',
    track_shininess: '',
    track_dirtiness: '',
    // Waxing data
    base_wax: '',
    glide_wax: '',
    kick_wax: '',
    technique: 'both',
    distance: '',
    notes: '',
    performance_rating: 5
  });

  useEffect(() => {
    loadEntries();
  }, []);

  // Apply weather filters whenever entries or filters change
  useEffect(() => {
    if (hasActiveWeatherFilters(weatherFilters)) {
      const filtered = filterByWeatherConditions(entries, weatherFilters);
      setFilteredEntries(filtered);
    } else {
      setFilteredEntries(entries);
    }
  }, [entries, weatherFilters]);

  const handleWeatherFilterChange = (filters) => {
    setWeatherFilters(filters);
  };

  const handleClearWeatherFilters = () => {
    setWeatherFilters({});
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await getMyWaxingDiary();
      setEntries(data);
      setFilteredEntries(data);
      setError(null);
      // Fetch shared status for each entry
      const sharedStatuses = await Promise.all(
        data.map(async (entry) => {
          const teams = await getSharedTeams(entry.id);
          return { id: entry.id, shared: teams.length > 0 };
        })
      );
      const sharedMapObj = {};
      sharedStatuses.forEach(({ id, shared }) => {
        sharedMapObj[id] = shared;
      });
      setSharedMap(sharedMapObj);
    } catch (err) {
      setError('Failed to load waxing diary entries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const entryData = {
        ...formData,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        humidity: formData.humidity ? parseFloat(formData.humidity) : null,
        distance: formData.distance ? parseFloat(formData.distance) : null,
        performance_rating: parseInt(formData.performance_rating)
      };

      if (editingEntry) {
        await updateWaxingDiaryEntry(editingEntry.id, entryData);
      } else {
        await addWaxingDiaryEntry(entryData);
      }

      setDialogOpen(false);
      setEditingEntry(null);
      resetForm();
      loadEntries();
    } catch (err) {
      setError('Failed to save waxing diary entry');
      console.error(err);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title || '',
      activity_type: entry.activity_type || 'training',
      location: entry.location || '',
      temperature: entry.temperature?.toString() || '',
      humidity: entry.humidity?.toString() || '',
      snow_conditions: entry.snow_conditions || '',
      snow_type: entry.snow_type || '',
      air_conditions: entry.air_conditions || '',
      base_wax: entry.base_wax || '',
      glide_wax: entry.glide_wax || '',
      kick_wax: entry.kick_wax || '',
      technique: entry.technique || 'both',
      distance: entry.distance?.toString() || '',
      notes: entry.notes || '',
      performance_rating: entry.performance_rating || 5
    });
    setDialogOpen(true);
  };

  const handleDelete = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this waxing diary entry?')) {
      try {
        await deleteWaxingDiaryEntry(entryId);
        loadEntries();
      } catch (err) {
        setError('Failed to delete waxing diary entry');
        console.error(err);
      }
    }
  };

  const handleShare = (entry) => {
    setSelectedEntry(entry);
    setShareModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      activity_type: 'training',
      location: '',
      temperature: '',
      humidity: '',
      snow_conditions: '',
      snow_type: '',
      air_conditions: '',
      base_wax: '',
      glide_wax: '',
      kick_wax: '',
      technique: 'both',
      distance: '',
      notes: '',
      performance_rating: 5
    });
  };

  const getActivityTypeColor = (type) => {
    switch (type) {
      case 'race': return 'error';
      case 'training': return 'primary';
      case 'test': return 'warning';
      default: return 'default';
    }
  };

  const getTechniqueColor = (technique) => {
    switch (technique) {
      case 'classic': return 'success';
      case 'skate': return 'info';
      case 'both': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          My Waxing Diary
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingEntry(null);
            resetForm();
            setDialogOpen(true);
          }}
        >
          Add Entry
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Weather Filter */}
      <WeatherFilter 
        onFilterChange={handleWeatherFilterChange}
        onClearFilters={handleClearWeatherFilters}
        compact={false}
        title="Filter Waxing Diary by Weather Conditions"
      />

      {filteredEntries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {entries.length === 0 ? 'No waxing diary entries yet' : 'No entries match your weather filters'}
          </Typography>
          <Typography color="text.secondary">
            {entries.length === 0 
              ? 'Start tracking your waxing strategies by adding your first entry.'
              : 'Try adjusting your weather filters to see more entries.'
            }
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredEntries.map((entry) => (
            <Paper key={entry.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {entry.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {new Date(entry.date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={entry.activity_type}
                    color={getActivityTypeColor(entry.activity_type)}
                    size="small"
                  />
                  <Chip
                    label={entry.technique}
                    color={getTechniqueColor(entry.technique)}
                    size="small"
                  />
                  {sharedMap[entry.id] && (
                    <Tooltip title="Shared with one or more teams">
                      <Chip label="Shared" color="success" size="small" />
                    </Tooltip>
                  )}
                  <IconButton size="small" onClick={() => handleShare(entry)}>
                    <ShareIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleEdit(entry)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(entry.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">View Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Location & Distance</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Location:</strong> {entry.location || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Distance:</strong> {entry.distance ? `${entry.distance}km` : 'N/A'}
                        </Typography>
                      </Box>
          <WeatherDataDisplay
            weatherData={entry}
            title="Weather Conditions"
            compact={true}
          />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Waxing</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Base Wax:</strong> {entry.base_wax || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Glide Wax:</strong> {entry.glide_wax || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Kick Wax:</strong> {entry.kick_wax || 'N/A'}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle2" gutterBottom>Performance</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={entry.performance_rating || 0} readOnly size="small" />
                        <Typography variant="body2">
                          ({entry.performance_rating || 0}/10)
                        </Typography>
                      </Box>
                    </Grid>
                    {entry.notes && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                        <Typography variant="body2">{entry.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEntry ? 'Edit Waxing Diary Entry' : 'Add Waxing Diary Entry'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Activity Type</InputLabel>
                  <Select
                    value={formData.activity_type}
                    onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
                    label="Activity Type"
                  >
                    <MenuItem value="race">Race</MenuItem>
                    <MenuItem value="training">Training</MenuItem>
                    <MenuItem value="test">Test</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Technique</InputLabel>
                  <Select
                    value={formData.technique}
                    onChange={(e) => setFormData({ ...formData, technique: e.target.value })}
                    label="Technique"
                  >
                    <MenuItem value="classic">Classic</MenuItem>
                    <MenuItem value="skate">Skate</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Distance (km)"
                  type="number"
                  value={formData.distance}
                  onChange={(e) => setFormData({ ...formData, distance: e.target.value })}
                />
              </Grid>
              
              {/* Weather Data Form */}
              <Grid item xs={12}>
        <WeatherDataForm
          weatherData={formData}
          onChange={setFormData}
          title="Weather Conditions"
          compact={true}
        />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Base Wax"
                  value={formData.base_wax}
                  onChange={(e) => setFormData({ ...formData, base_wax: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Glide Wax"
                  value={formData.glide_wax}
                  onChange={(e) => setFormData({ ...formData, glide_wax: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Kick Wax"
                  value={formData.kick_wax}
                  onChange={(e) => setFormData({ ...formData, kick_wax: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography component="legend">Performance Rating:</Typography>
                  <Rating
                    value={formData.performance_rating}
                    onChange={(_, newValue) => setFormData({ ...formData, performance_rating: newValue })}
                  />
                  <Typography>({formData.performance_rating}/10)</Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingEntry ? 'Update' : 'Add'} Entry
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Share Modal */}
      <ShareWaxingEntryModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        entry={selectedEntry}
      />
    </Box>
  );
} 